import { useState } from "react";
import { useTranslation } from "react-i18next";
import RichText from "components/basic/richText";
import { Box, useTheme } from "@mui/material";
import Button from "components/basic/button";
import Checkbox from "components/basic/checkbox";
import Typography from "components/basic/typography";
import { useAcceptVendorAgreementMutation } from "generated/graphql";
import { useSnack } from "hooks/useSnack";

const AGREEMENT_URL = "/vendor-agreement";

interface AgreementStepProps {
  onAccepted: () => void;
}

/** The Vendor Service Agreement every vendor accepts before onboarding. */
const AgreementStep: React.FC<AgreementStepProps> = ({ onAccepted }) => {
  const theme = useTheme();
  const showSnack = useSnack();
  const [, accept] = useAcceptVendorAgreementMutation();
  const [agreed, setAgreed] = useState(false);
  const { t } = useTranslation("onboarding");
  const [saving, setSaving] = useState(false);

  const handleAccept = async () => {
    setSaving(true);
    const response = await accept({});
    setSaving(false);
    const error =
      response.data?.acceptVendorAgreement.errors?.[0]?.message ??
      response.error?.message;
    if (error) {
      showSnack(error, "error");
      return;
    }
    onAccepted();
  };

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      <Typography variant="body1">{t("agreement.intro")}</Typography>
      <Checkbox
        isChecked={agreed}
        onChange={setAgreed}
        label={
          <RichText
            t={t}
            i18nKey="agreement.agree"
            components={{
              a: (
                // eslint-disable-next-line jsx-a11y/anchor-has-content -- Trans fills it
                <a
                  href={AGREEMENT_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                />
              ),
            }}
          />
        }
      />
      <Button
        variant="contained"
        color="warning"
        style={{ color: theme.palette.common.white, alignSelf: "flex-start" }}
        onClick={handleAccept}
        loading={saving}
        disabled={!agreed || saving}
      >
        {t("agreement.accept")}
      </Button>
    </Box>
  );
};

export default AgreementStep;
